<template>
  <b-overlay :show="loading" rounded="sm" no-fade>
    <b-card class="m-0 p-0">
      <b-row class="mb-1 align-items-center">
        <b-col cols="12" md="3" class="d-flex align-items-center mb-1 mb-md-0">
          <h4 class="mb-0">Director Event Scheduler</h4>
        </b-col>
        <b-col cols="12" md="9" class="d-flex align-items-center justify-content-end flex-wrap">
          <flat-pickr
            v-model="dateRange"
            class="form-control mr-1 mb-50"
            style="max-width: 230px;"
            :config="rangeConfig"
            placeholder="Filter by date range"
            @on-change="onRangeChange"
          />
          <v-select
            v-model="filters.status"
            :options="statusOptions"
            :reduce="(o) => o.value"
            placeholder="Status"
            class="mr-1 mb-50"
            style="min-width: 150px;"
            @input="load"
          />
          <v-select
            v-model="filters.event_type"
            :options="typeOptions"
            :reduce="(o) => o.value"
            placeholder="Type"
            class="mr-1 mb-50"
            style="min-width: 160px;"
            @input="load"
          />
          <v-select
            v-model="filters.working_group_id"
            :options="workingGroups"
            label="group_name"
            :reduce="(o) => o.id"
            placeholder="Working group"
            class="mr-1 mb-50"
            style="min-width: 200px;"
            @input="load"
          />
          <b-form-input
            v-model="tableFilter"
            class="d-inline-block mr-1 mb-50"
            style="max-width: 200px;"
            placeholder="Search in table..."
          />
          <b-button
            v-if="$Can('create_governance_event')"
            variant="primary"
            class="mb-50"
            :to="{ name: 'governance-event-add' }"
          >
            <feather-icon icon="PlusIcon" class="mr-50" />
            Add Event
          </b-button>
        </b-col>
      </b-row>

      <b-table
        striped
        hover
        responsive
        show-empty
        :small="true"
        :items="events"
        :fields="fields"
        :filter="tableFilter"
        :current-page="currentPage"
        :per-page="perPage"
        empty-text="No governance events found"
        @filtered="onFiltered"
      >
        <template #cell(start_at)="{ item }">
          <div v-for="(p, idx) in govParts(item)" :key="p.id || idx" class="text-nowrap">
            <small v-if="govParts(item).length > 1" class="text-muted">Part {{ p.sequence_no || idx + 1 }}: </small>{{ govFormatPartRange(p, item.is_all_day) }}
          </div>
        </template>

        <template #cell(title)="{ item }">
          <b-link :to="{ name: 'governance-event-view', params: { id: item.id } }" class="font-weight-bold">
            {{ item.title }}
          </b-link>
          <b-badge v-if="govParts(item).length > 1" variant="light-info" class="ml-50">{{ govParts(item).length }} parts</b-badge>
          <small class="d-block text-muted" v-if="item.location">{{ item.location }}</small>
          <small class="d-block text-muted" v-else-if="item.meeting_url">Online</small>
        </template>

        <template #cell(event_type)="{ item }">
          {{ govTypeLabel(item.event_type) }}
        </template>

        <template #cell(working_group)="{ item }">
          {{ item.working_group ? item.working_group.group_name : "-" }}
        </template>

        <template #cell(status)="{ item }">
          <b-badge :variant="govStatusVariant(item.status)" class="text-capitalize">{{ item.status }}</b-badge>
        </template>

        <template #cell(invitees)="{ item }">
          <span :title="`${item.accepted_count || 0} accepted, ${item.declined_count || 0} declined`">
            {{ item.accepted_count || 0 }} / {{ item.invitees_count || 0 }}
          </span>
        </template>

        <template #cell(my_response)="{ item }">
          <b-badge v-if="item.my_response" :variant="govResponseVariant(item.my_response)" class="text-capitalize">
            {{ item.my_response }}
          </b-badge>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(actions)="{ item }">
          <div class="text-nowrap">
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-50"
              :to="{ name: 'governance-event-view', params: { id: item.id } }"
              title="View"
            >
              <feather-icon icon="EyeIcon" size="14" />
            </b-button>
            <b-button
              v-if="$Can('edit_governance_event')"
              size="sm"
              variant="outline-warning"
              class="mr-50"
              :to="{ name: 'governance-event-edit', params: { id: item.id } }"
              title="Edit"
            >
              <feather-icon icon="EditIcon" size="14" />
            </b-button>
            <b-button
              v-if="$Can('delete_governance_event')"
              size="sm"
              variant="outline-danger"
              title="Delete"
              @click="confirmDelete(item)"
            >
              <feather-icon icon="TrashIcon" size="14" />
            </b-button>
          </div>
        </template>
      </b-table>

      <b-row class="mt-1">
        <b-col cols="12" md="6" class="d-flex align-items-center">
          <span class="text-muted">Showing {{ totalRows }} event(s)</span>
        </b-col>
        <b-col cols="12" md="6" class="d-flex align-items-center justify-content-end">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0"
          />
        </b-col>
      </b-row>
    </b-card>
  </b-overlay>
</template>

<script>
import store from "@/store";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { onUnmounted } from "@vue/composition-api";
import governanceMixin, { registerGovernanceStore, unregisterGovernanceStore } from "./governanceMixin";

export default {
  name: "GovernanceEvents",
  components: { vSelect, flatPickr },
  mixins: [governanceMixin],
  setup() {
    registerGovernanceStore();
    onUnmounted(() => unregisterGovernanceStore());
  },
  data() {
    return {
      loading: false,
      events: [],
      workingGroups: [],
      enums: { event_types: [], event_statuses: [] },
      dateRange: null,
      rangeConfig: { mode: "range", dateFormat: "Y-m-d", altInput: true, altFormat: "d/m/Y" },
      filters: { from: null, to: null, status: null, event_type: null, working_group_id: null },
      tableFilter: null,
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      fields: [
        { key: "start_at", label: "When", sortable: true },
        { key: "title", label: "Title", sortable: true },
        { key: "event_type", label: "Type", sortable: true },
        { key: "working_group", label: "Working Group" },
        { key: "status", label: "Status", sortable: true },
        { key: "invitees", label: "Accepted / Invited", class: "text-center" },
        { key: "my_response", label: "My Response", class: "text-center" },
        { key: "actions", label: "Actions", class: "text-center" },
      ],
    };
  },
  computed: {
    statusOptions() {
      return this.enums.event_statuses.map((s) => ({ value: s, label: this.govCapitalize(s) }));
    },
    typeOptions() {
      return this.enums.event_types.map((t) => ({ value: t, label: this.govTypeLabel(t) }));
    },
  },
  mounted() {
    this.loadOptions();
    this.load();
  },
  methods: {
    loadOptions() {
      store.dispatch("governance/fetchOptions").then((res) => {
        this.workingGroups = res.data.workingGroups || [];
        this.enums = res.data.enums || this.enums;
      });
    },
    onRangeChange(selectedDates) {
      if (selectedDates.length === 2) {
        this.filters.from = this.toYmd(selectedDates[0]);
        this.filters.to = this.toYmd(selectedDates[1]);
        this.load();
      } else if (selectedDates.length === 0) {
        this.filters.from = null;
        this.filters.to = null;
        this.load();
      }
    },
    toYmd(d) {
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
    load() {
      this.loading = true;
      const params = {};
      Object.keys(this.filters).forEach((k) => {
        if (this.filters[k] !== null && this.filters[k] !== "") params[k] = this.filters[k];
      });
      store
        .dispatch("governance/fetchEvents", params)
        .then((res) => {
          this.events = res.data || [];
          this.totalRows = this.events.length;
        })
        .catch((error) => this.govToast("Could not load events", "danger", this.govErrorMessage(error)))
        .finally(() => {
          this.loading = false;
        });
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    confirmDelete(item) {
      this.$bvModal
        .msgBoxConfirm(`Delete "${item.title}"? Invitees of a scheduled event will be emailed a cancellation.`, {
          title: "Delete governance event",
          okVariant: "danger",
          okTitle: "Delete",
          cancelTitle: "Cancel",
          centered: true,
        })
        .then((ok) => {
          if (!ok) return;
          this.loading = true;
          store
            .dispatch("governance/deleteEvent", item.id)
            .then(() => {
              this.govToast("Event deleted");
              this.load();
            })
            .catch((error) => {
              this.loading = false;
              this.govToast("Could not delete event", "danger", this.govErrorMessage(error));
            });
        });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
